// v1
// Path: frontend/src/components/QuickBooksSync/DataDictionaryModal.jsx
// Inputs: token, onClose
// Outputs: React JSX modal allowing search, browsing, tagging, and note editing across all Passport tables and fields
// Summary: Database-backed Passport ERP data dictionary browser with field tagging, primary key indices, and custom notes persistence.

import React, { useState, useEffect } from 'react';


// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

export const DataDictionaryModal = ({ token, onClose }) => {
    const [tables, setTables] = useState([]);
    const [loadingTables, setLoadingTables] = useState(true);
    const [selectedTable, setSelectedTable] = useState(null);
    const [fields, setFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);
    const [tableSearch, setTableSearch] = useState('');
    const [moduleFilter, setModuleFilter] = useState('ALL');
    const [fieldSearch, setFieldSearch] = useState('');

    // Editing field state
    const [editingFieldId, setEditingFieldId] = useState(null);
    const [editForm, setEditForm] = useState({ tags: [], notes: '', fieldDescription: '', isPrimaryKey: false, primaryKeyOrder: null });
    const [newTagInput, setNewTagInput] = useState('');
    const [savingField, setSavingField] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);

    // Suggested quick tags
    const commonTags = [
        'primary key.1', 'primary key.2', 'primary key', 'account number', 'sub account number',
        'id', 'customer', 'vendor', 'item', 'monetary', 'date', 'status flag'
    ];

    // Load tables list
    useEffect(() => {
        const fetchTables = async () => {
            setLoadingTables(true);
            try {
                const res = await fetch('/api/sync/data-dictionary/tables', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await unbox(res);
                    setTables(Array.isArray(data) ? data : []);
                    if (data.length > 0) {
                        // Auto-select first or CUSFIL / CHARTF
                        const def = data.find(t => t.TableName === 'CUSFIL') || data.find(t => t.TableName === 'CHARTF') || data[0];
                        setSelectedTable(def);
                    }
                }
            } catch (err) {
                console.error('[DataDictionaryModal] Error loading tables:', err);
            } finally {
                setLoadingTables(false);
            }
        };
        fetchTables();
    }, [token]);

    // Load fields for selected table
    useEffect(() => {
        if (!selectedTable) return;
        const fetchFields = async () => {
            setLoadingFields(true);
            try {
                const res = await fetch(`/api/sync/data-dictionary/fields/${selectedTable.TableName}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await unbox(res);
                    setFields(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error('[DataDictionaryModal] Error loading fields:', err);
            } finally {
                setLoadingFields(false);
            }
        };
        fetchFields();
    }, [selectedTable, token]);

    const distinctModules = ['ALL', ...new Set(tables.map(t => t.Module).filter(Boolean))];

    const filteredTables = tables.filter(t => {
        if (moduleFilter !== 'ALL' && t.Module !== moduleFilter) return false;
        if (tableSearch.trim()) {
            const q = tableSearch.toLowerCase();
            return t.TableName.toLowerCase().includes(q) || (t.Description || '').toLowerCase().includes(q);
        }
        return true;
    });

    const filteredFields = fields.filter(f => {
        if (!fieldSearch.trim()) return true;
        const q = fieldSearch.toLowerCase();
        return f.SQLColumnName.toLowerCase().includes(q) ||
               (f.CobolFieldName || '').toLowerCase().includes(q) ||
               (f.FieldDescription || '').toLowerCase().includes(q) ||
               (f.tags || []).some(t => t.toLowerCase().includes(q));
    });

    const startEditField = (f) => {
        setEditingFieldId(f.FieldID);
        setEditForm({
            tags: f.tags ? [...f.tags] : [],
            notes: f.Notes || '',
            fieldDescription: f.FieldDescription || '',
            isPrimaryKey: !!f.IsPrimaryKey,
            primaryKeyOrder: f.PrimaryKeyOrder || null
        });
        setNewTagInput('');
        setStatusMessage(null);
    };

    const handleAddTag = (tagToAdd) => {
        const tag = (tagToAdd || newTagInput).trim().toLowerCase();
        if (!tag || editForm.tags.includes(tag)) return;
        setEditForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
        setNewTagInput('');
    };

    const handleRemoveTag = (tagToRemove) => {
        setEditForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
    };

    const handleSaveField = async (fieldId) => {
        setSavingField(true);
        setStatusMessage(null);
        try {
            const res = await fetch(`/api/sync/data-dictionary/fields/${fieldId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editForm)
            });
            if (res.ok) {
                setStatusMessage({ type: 'success', text: 'Field updated and persisted to database!' });
                setFields(prev => prev.map(f => f.FieldID === fieldId ? {
                    ...f,
                    tags: editForm.tags,
                    Notes: editForm.notes,
                    FieldDescription: editForm.fieldDescription,
                    IsPrimaryKey: editForm.isPrimaryKey,
                    PrimaryKeyOrder: editForm.primaryKeyOrder
                } : f));
                setEditingFieldId(null);
            } else {
                const err = await unbox(res);
                setStatusMessage({ type: 'error', text: err.error || 'Failed to update field.' });
            }
        } catch (err) {
            setStatusMessage({ type: 'error', text: err.message });
        } finally {
            setSavingField(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col h-[90vh] overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-850">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                            <span className="material-symbols-outlined text-[24px]">menu_book</span>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white tracking-wide">
                                Passport ERP Database Dictionary & Field Tagger
                            </h3>
                            <p className="text-xs text-slate-400">
                                Browse 221 Passport tables and 7,844 fields. Add semantic tags (e.g. `primary key.1`, `account number`, `id`) and developer notes.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {statusMessage && (
                    <div className={`px-6 py-2.5 text-xs flex items-center justify-between ${statusMessage.type === 'success' ? 'bg-emerald-950/60 border-b border-emerald-800 text-emerald-300' : 'bg-red-950/60 border-b border-red-800 text-red-300'}`}>
                        <span>{statusMessage.text}</span>
                        <button onClick={() => setStatusMessage(null)}>
                            <span className="material-symbols-outlined text-[16px]">close</span>
                        </button>
                    </div>
                )}

                {/* Main Two-Column Layout */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Left Pane: Tables List */}
                    <div className="w-80 border-r border-slate-800 flex flex-col bg-slate-950/40">
                        {/* Search & Filter */}
                        <div className="p-3 border-b border-slate-800 space-y-2">
                            <div className="relative">
                                <span className="material-symbols-outlined absolute left-2.5 top-2 text-slate-400 text-[16px]">search</span>
                                <input
                                    type="text"
                                    placeholder="Search tables..."
                                    value={tableSearch}
                                    onChange={e => setTableSearch(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-8 pr-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                                />
                            </div>
                            <select
                                value={moduleFilter}
                                onChange={e => setModuleFilter(e.target.value)}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-xs text-slate-300 focus:outline-none focus:border-blue-500"
                            >
                                {distinctModules.map(m => (
                                    <option key={m} value={m}>{m} ({m === 'ALL' ? tables.length : tables.filter(t => t.Module === m).length})</option>
                                ))}
                            </select>
                        </div>

                        {/* Tables Scroll List */}
                        <div className="flex-1 overflow-y-auto divide-y divide-slate-800/60">
                            {loadingTables ? (
                                <div className="p-6 text-center text-slate-500 text-xs">
                                    <span className="material-symbols-outlined animate-spin mb-1">sync</span>
                                    <p>Loading tables...</p>
                                </div>
                            ) : filteredTables.length === 0 ? (
                                <div className="p-6 text-center text-slate-500 text-xs">No tables matched.</div>
                            ) : (
                                filteredTables.map(t => {
                                    const isSelected = selectedTable?.TableID === t.TableID;
                                    return (
                                        <button
                                            key={t.TableID}
                                            onClick={() => { setSelectedTable(t); setEditingFieldId(null); }}
                                            className={`w-full text-left p-3 flex flex-col gap-0.5 transition-colors ${isSelected ? 'bg-blue-600/20 border-l-4 border-blue-500 text-white' : 'hover:bg-slate-800/60 text-slate-300'}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="font-mono font-bold text-xs text-emerald-300">{t.TableName}</span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 font-mono">
                                                    {t.FieldCount || 0} cols
                                                </span>
                                            </div>
                                            <span className="text-[11px] text-slate-400 truncate">{t.Description || `${t.TableName} Table`}</span>
                                            <span className="text-[9px] text-blue-400 uppercase font-bold tracking-wider">{t.Module}</span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Right Pane: Fields Inspector & Tagger */}
                    <div className="flex-1 flex flex-col overflow-hidden bg-slate-900">
                        {/* Table Header Details */}
                        {selectedTable && (
                            <div className="p-4 border-b border-slate-800 bg-slate-850/60 flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h4 className="text-base font-bold text-white font-mono">{selectedTable.TableName}</h4>
                                        <span className="px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[11px] font-semibold">
                                            {selectedTable.Module}
                                        </span>
                                        <span className="text-xs text-slate-400">({fields.length} Columns Defined)</span>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-0.5">{selectedTable.Description}</p>
                                </div>

                                <div className="relative w-64">
                                    <span className="material-symbols-outlined absolute left-2.5 top-2 text-slate-400 text-[15px]">search</span>
                                    <input
                                        type="text"
                                        placeholder="Search columns or tags..."
                                        value={fieldSearch}
                                        onChange={e => setFieldSearch(e.target.value)}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-8 pr-2.5 py-1.5 text-xs text-white placeholder-slate-500"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Fields List */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {loadingFields ? (
                                <div className="py-16 text-center text-slate-500 text-xs">
                                    <span className="material-symbols-outlined animate-spin text-3xl mb-2">sync</span>
                                    <p>Loading table fields...</p>
                                </div>
                            ) : filteredFields.length === 0 ? (
                                <div className="text-center py-12 text-slate-500 text-xs">No fields matched the search.</div>
                            ) : (
                                filteredFields.map(f => {
                                    const isEditing = editingFieldId === f.FieldID;
                                    return (
                                        <div
                                            key={f.FieldID}
                                            className={`p-3.5 rounded-xl border transition-all ${isEditing ? 'bg-slate-850 border-blue-500 shadow-md ring-1 ring-blue-500/30' : 'bg-slate-800/80 border-slate-700/80 hover:border-slate-600'}`}
                                        >
                                            {!isEditing ? (
                                                <div className="flex flex-wrap items-start justify-between gap-3">
                                                    <div className="space-y-1.5 flex-1 min-w-[280px]">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-mono font-bold text-xs text-white bg-slate-900 px-2 py-0.5 rounded border border-slate-700">
                                                                {f.SQLColumnName}
                                                            </span>
                                                            {f.CobolFieldName && (
                                                                <span className="font-mono text-[11px] text-slate-400">
                                                                    ({f.CobolFieldName})
                                                                </span>
                                                            )}
                                                            {f.IsPrimaryKey && (
                                                                <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-bold">
                                                                    PK {f.PrimaryKeyOrder ? `.${f.PrimaryKeyOrder}` : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-slate-300 font-medium">{f.FieldDescription || 'No description provided'}</p>
                                                        {f.Notes && (
                                                            <p className="text-[11px] text-slate-400 italic bg-slate-900/60 p-1.5 rounded border border-slate-800">
                                                                Notes: {f.Notes}
                                                            </p>
                                                        )}

                                                        {/* Tags */}
                                                        <div className="flex items-center gap-1.5 flex-wrap pt-1">
                                                            {(f.tags || []).length === 0 ? (
                                                                <span className="text-[10px] text-slate-500 italic">No tags</span>
                                                            ) : (
                                                                f.tags.map(t => (
                                                                    <span
                                                                        key={t}
                                                                        className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${t.startsWith('primary key') ? 'bg-amber-950/70 text-amber-300 border-amber-800' : t.includes('acct') || t.includes('account') ? 'bg-blue-950/70 text-blue-300 border-blue-800' : t === 'id' ? 'bg-purple-950/70 text-purple-300 border-purple-800' : 'bg-slate-700 text-slate-300 border-slate-600'}`}
                                                                    >
                                                                        #{t}
                                                                    </span>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => startEditField(f)}
                                                        className="px-2.5 py-1 bg-slate-750 hover:bg-slate-700 text-slate-200 rounded text-xs font-semibold transition-colors flex items-center gap-1 border border-slate-600"
                                                    >
                                                        <span className="material-symbols-outlined text-[14px]">label</span>
                                                        Edit Tags / Notes
                                                    </button>
                                                </div>
                                            ) : (
                                                /* Edit Mode Form */
                                                <div className="space-y-3 text-xs">
                                                    <div className="flex items-center justify-between border-b border-slate-700 pb-2">
                                                        <span className="font-mono font-bold text-emerald-300">
                                                            Editing {f.SQLColumnName}
                                                        </span>
                                                        <span className="text-slate-400 text-[11px]">{f.CobolFieldName}</span>
                                                    </div>

                                                    <div>
                                                        <label className="block text-slate-400 mb-1 font-semibold">Functional Description</label>
                                                        <input
                                                            type="text"
                                                            value={editForm.fieldDescription}
                                                            onChange={e => setEditForm({ ...editForm, fieldDescription: e.target.value })}
                                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                                                        />
                                                    </div>

                                                    {/* Tags Editor */}
                                                    <div>
                                                        <label className="block text-slate-400 mb-1 font-semibold">Tags (e.g. `primary key.1`, `account number`, `id`)</label>
                                                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                                            {editForm.tags.map(t => (
                                                                <span key={t} className="px-2 py-0.5 rounded bg-blue-900/60 text-blue-200 border border-blue-700 text-xs flex items-center gap-1">
                                                                    #{t}
                                                                    <button type="button" onClick={() => handleRemoveTag(t)} className="text-blue-300 hover:text-white">
                                                                        &times;
                                                                    </button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                placeholder="Type new tag and press Enter..."
                                                                value={newTagInput}
                                                                onChange={e => setNewTagInput(e.target.value)}
                                                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                                                                className="flex-1 bg-slate-900 border border-slate-700 rounded-lg p-1.5 text-white text-xs"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => handleAddTag()}
                                                                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-xs"
                                                            >
                                                                Add Tag
                                                            </button>
                                                        </div>

                                                        {/* Quick Suggested Tag Buttons */}
                                                        <div className="flex items-center gap-1.5 flex-wrap mt-2">
                                                            <span className="text-[10px] text-slate-500">Quick add:</span>
                                                            {commonTags.filter(ct => !editForm.tags.includes(ct)).slice(0, 7).map(ct => (
                                                                <button
                                                                    key={ct}
                                                                    type="button"
                                                                    onClick={() => handleAddTag(ct)}
                                                                    className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] border border-slate-700"
                                                                >
                                                                    +{ct}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Primary Key Check */}
                                                    <div className="flex items-center gap-4 bg-slate-900/50 p-2.5 rounded-lg border border-slate-800">
                                                        <label className="flex items-center gap-2 text-slate-300 font-semibold cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={editForm.isPrimaryKey}
                                                                onChange={e => setEditForm({ ...editForm, isPrimaryKey: e.target.checked })}
                                                                className="w-4 h-4 rounded text-blue-600 bg-slate-900 border-slate-700"
                                                            />
                                                            Is Primary Key Field
                                                        </label>
                                                        {editForm.isPrimaryKey && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-slate-400">Order (e.g. 1 for PK.1, 2 for PK.2):</span>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max="10"
                                                                    value={editForm.primaryKeyOrder || 1}
                                                                    onChange={e => setEditForm({ ...editForm, primaryKeyOrder: parseInt(e.target.value, 10) || 1 })}
                                                                    className="w-16 bg-slate-900 border border-slate-700 rounded p-1 text-center text-white"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Notes */}
                                                    <div>
                                                        <label className="block text-slate-400 mb-1 font-semibold">Technical / Modernization Notes</label>
                                                        <textarea
                                                            rows="2"
                                                            value={editForm.notes}
                                                            onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                                                            placeholder="Add any notes on Acu4GL quirks, relationship joins, or business logic..."
                                                            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                                                        />
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex justify-end gap-2 pt-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingFieldId(null)}
                                                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            disabled={savingField}
                                                            onClick={() => handleSaveField(f.FieldID)}
                                                            className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-bold flex items-center gap-1.5"
                                                        >
                                                            {savingField ? (
                                                                <>
                                                                    <span className="material-symbols-outlined text-[14px] animate-spin">sync</span>
                                                                    Saving...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span className="material-symbols-outlined text-[14px]">save</span>
                                                                    Save Changes
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-3 border-t border-slate-800 bg-slate-850 flex justify-between items-center text-xs text-slate-400">
                    <div>
                        Stored in SQL Server tables <code>Passport.Tables</code> and <code>Passport.Fields</code>.
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg font-semibold transition-colors border border-slate-700"
                    >
                        Close Dictionary
                    </button>
                </div>
            </div>
        </div>
    );
};
